import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MinusCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { getInitialGreeting } from '../utils/chatbot/messageFormatter';
import { createUserMessage, createBotMessage, processMessage } from '../services/chatbot/messageService';
import { Message } from '../types/chat';
import ChatHeader from './Chat/ChatHeader';
import ChatMessages from './Chat/ChatMessages';
import ChatInput from './Chat/ChatInput';
import { useMediaQuery } from '../hooks/useMediaQuery';

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  const [conversationHistory, setConversationHistory] = useState<any[]>([]);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen && !messages.length) {
      const welcomeMessage = createBotMessage(
        getInitialGreeting(user?.displayName || user?.email)
      );
      setMessages([welcomeMessage]);
    }
  }, [isOpen, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = useCallback(async (text: string) => {
    const userMessage = createUserMessage(text);
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: text }
    ];
    setConversationHistory(updatedHistory);

    try {
      const response = await processMessage(text, updatedHistory);
      const botMessage = createBotMessage(response);
      
      setMessages(prev => [...prev, botMessage]);
      setConversationHistory(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage = createBotMessage(
        'Désolé, je rencontre des difficultés techniques. Veuillez réessayer.'
      );
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  }, [conversationHistory]);

  // Hide chat bot on mobile
  if (isMobile) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <AnimatePresence>
        {isOpen && !isMinimized && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg mb-4 w-[380px] max-h-[600px] flex flex-col"
          >
            <ChatHeader
              onMinimize={() => setIsMinimized(true)}
              onClose={() => setIsOpen(false)}
            />
            
            <ChatMessages
              messages={messages}
              isTyping={isTyping}
              messagesEndRef={messagesEndRef}
            />
            
            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isTyping}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setIsOpen(true);
          setIsMinimized(false);
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="relative bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
      >
        {isMinimized ? (
          <MinusCircle className="w-6 h-6" />
        ) : (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
        )}
        
        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg whitespace-nowrap"
            >
              Assistant virtuel Shopiweb
              <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default React.memo(ChatBot);